<template>
  <div>
    <div class="d-flex mc-list-header mt-3 mb-2">
      <h4 class="card-title flex-grow-1">
        {{ $t('fleetTask.notification.title') }}
      </h4>
    </div>
    <b-card>
      <div class="d-flex justify-content-between">
        <div>
          <p class="teal-300">{{ $t('fleetTask.notification.recipientCustomer') }}</p>
        </div>
        <div class="d-flex gap-2 mb-2">
          <div class="d-flex align-items-center gap-2 mb-2">
            <p class="mb-0">{{ customerName }}</p>
            <McInfoTooltip
              v-if="customerName === 'Banyak Pelanggan' && !notificationAvailable"
              id="multi_customer"
              :tooltip="$t('fleetTask.notification.multiCustomerTooltip')"
            />
          </div>
        </div>
      </div>

      <div v-if="!notificationAvailable">
        <McAlert variant="error" :withGap="false" withIcon customIcon="icon-fill_bell-off">
          <template #label>
            <div>
              <span>
                <p>{{ $t('fleetTask.notification.unavailable.title') }}</p>
              </span>
              <p class="teal-300 mb-0 alert-message">
                {{
                  $t('fleetTask.notification.unavailable.description')
                }}
              </p>
            </div>
          </template>
        </McAlert>
      </div>

      <div v-else-if="notificationAvailableNoPhone">
        <McAlert variant="error" :withGap="false" withIcon customIcon="icon-fill_bell-off">
          <template #label>
            <div>
              <span>
                <p>{{ $t('fleetTask.notification.noPhone.title') }}</p>
              </span>
              <p class="teal-300 mb-0 alert-message">
                {{
                  $t('fleetTask.notification.noPhone.description')
                }}
              </p>
            </div>
          </template>
        </McAlert>
      </div>

      <div v-else-if="isStatusMatch && notification">
        <b-card class="border rounded shadow-sm bg-white custom-card">
          <div class="d-flex justify-content-between">
            <div class="notif-icon">
              <div class="circle-bg d-flex align-items-center justify-content-center">
                <i class="icon-fill_bell bell-icon"></i>
                <span class="badge-dot"></span>
              </div>
            </div>
            <div>
              <p>{{ $t('fleetTask.notification.active.title') }}</p>
              <small class="text-muted">
                {{
                  $t('fleetTask.notification.active.description')
                }}
              </small>
            </div>
          </div>

          <div v-if="notification">
            <hr />
            <div class="display-phone-number">
              <p class="text-muted">{{ $t('fleetTask.notification.customerWhatsappNumber') }}</p>
              <p>{{ `+62 ${customerPhone}` }}</p>
            </div>
          </div>
        </b-card>
      </div>

      <div v-else-if="isStatusMatch && !notification">
        <McAlert variant="info" :withGap="false" withIcon customIcon="icon-fill_bell-off">
          <template #label>
            <div>
              <span>
                <p>{{ $t('fleetTask.notification.inactive.title') }}</p>
              </span>
              <p class="teal-300 mb-0 alert-message">
                {{
                  $t('fleetTask.notification.inactive.description')
                }}
              </p>
              <a class="mc-primary" @click="openDeliveryHub">
                {{ $t('fleetTask.notification.openDeliveryHub') }}
                <i class="mc-icon icon-fill_external-link"></i>
              </a>
            </div>
          </template>
        </McAlert>
      </div>

      <div v-else>
        <b-card class="border rounded shadow-sm bg-white custom-card">
          <div class="d-flex justify-content-between">
            <div class="notif-icon">
              <div class="circle-bg d-flex align-items-center justify-content-center">
                <i class="icon-fill_bell bell-icon"></i>
                <span class="badge-dot"></span>
              </div>
            </div>
            <div>
              <p>{{ $t('fleetTask.notification.enable.title') }}</p>
              <small class="text-muted">
                {{
                  $t('fleetTask.notification.enable.description')
                }}
              </small>
            </div>
            <div class="d-flex gap-2 mb-2">
              <div class="form-check form-switch form-switch-sm">
                <input
                  v-model="notification"
                  class="form-check-input"
                  type="checkbox"
                  :disabled="isUserGroupNull || disabled"
                />
              </div>
            </div>
          </div>

          <div v-if="notification">
            <hr />
            <FormulateForm :errors="formErrors">
              <FormulateInput
                v-model="customerPhone"
                name="customerPhone"
                prefix
                type="text"
                :placeholder="$t('fleetTask.notification.customerWhatsappPlaceholder')"
                :disabled="isUserGroupNull || disabled"
              >
                <template #label>
                  <div class="d-flex justify-content-between align-items-center">
                    <label class="text-muted">{{ $t('fleetTask.notification.customerWhatsappNumber') }}</label>
                  </div>
                </template>
                <template #prefix>
                  <div class="formulate-input-prefix">
                    <span>{{ '+62' }}</span>
                  </div>
                </template>
              </FormulateInput>
            </FormulateForm>

            <div class="mt-2">
              <McAlert variant="info" :withGap="false" withIcon>
                <template #label>
                  <div>
                    <span>
                      <p>{{ $t('fleetTask.notification.activationGuide.title') }}</p>
                    </span>
                    <p class="teal-300 mb-0 alert-message">
                      {{
                        $t('fleetTask.notification.activationGuide.description')
                      }}
                    </p>

                    <div class="d-flex justify-content-between mt-2">
                      <McButton
                        variant="secondary-light"
                        size="sm"
                        :text="$t('fleetTask.notification.activationGuide.button')"
                        icon="icon-fill_arrow-right"
                        iconPosition="right"
                        @click="openNotificationGuide"
                      />
                      <a class="mc-primary" @click="openDeliveryHub">
                        {{ $t('fleetTask.notification.openDeliveryHub') }}
                        <i class="mc-icon icon-fill_external-link"></i>
                      </a>
                    </div>
                  </div>
                </template>
              </McAlert>
            </div>
          </div>
        </b-card>
      </div>
    </b-card>

    <GuideNotification />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { Helper } from '@/mixin/helper'
import McInfoTooltip from '@/components/McInfoTooltip'
import McAlert from '@/components/McAlert'
import McButton from '@/components/McButton'
import GuideNotification from '@/router/views/fleet-task/components/Notification/Modal/GuideNotification'

export default {
  components: {
    McInfoTooltip,
    McAlert,
    McButton,
    GuideNotification,
  },
  mixins: [Helper],
  props: {
    hasErrorNotification: {
      type: Object,
      default: () => ({
        show: false,
        message: null,
      }),
    },
    isUserGroupNull: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localNotification: false,
      localCustomerPhone: '',
      isEditingPhone: false,
      showErrorMarker: false,
      formErrors: {},
    }
  },
  computed: {
    ...mapState('fleetTask', ['fleetTaskStatus', 'fleetTaskDetail', 'selectedDos']),
    multiCustomer() {
      return false
    },
    hasDoTransit() {
      return false
    },
    customerName() {
      if (this.selectedDos.length <= 0) {
        return '-'
      }
      return this.fleetTaskDetail?.customer?.name || 'Banyak Pelanggan'
    },
    isStatusMatch() {
      const allowedStatuses = [
        'STARTED',
        'ENDED',
        'WAITING_FOR_VENDOR',
        'ACCEPTED_BY_VENDOR',
        'REJECTED_BY_VENDOR',
      ]
      if (this.fleetTaskDetail?.vendor?.id) {
        allowedStatuses.push('SCHEDULED')
      }
      return allowedStatuses.includes(this.statusFleetOrder)
    },
    notificationAvailable() {
      if (this.isStatusMatch && this.customerPhone) {
        return true
      }
      return this.fleetTaskDetail?.notification_available
    },
    notificationAvailableNoPhone() {
      return this.isStatusMatch && this.fleetTaskDetail?.notification_available && !this.customerPhone
    },
    notification: {
      get() {
        return this.localNotification || this.fleetTaskDetail?.notification_enabled || false
      },
      set(val) {
        this.localNotification = val

        if (val) {
          if (!this.customerPhone || this.customerPhone.trim() === '') {
            this.$set(this.formErrors, 'customerPhone', 'Wajib diisi')
          } else {
            this.$delete(this.formErrors, 'customerPhone')
          }
        } else {
          this.$delete(this.formErrors, 'customerPhone')
        }

        if (!val || (this.customerPhone && this.customerPhone.trim() !== '')) {
          this.$emit('notification', val)
        }
      },
    },
    customerPhone: {
      get() {
        return this.localCustomerPhone
      },
      set(val) {
        this.isEditingPhone = true

        if (val.trim() === '') {
          this.showErrorMarker = true
          this.$set(this.formErrors, 'customerPhone', this.$t('global.validation.required'))
          this.localCustomerPhone = ''
          return
        }

        this.showErrorMarker = false
        this.$delete(this.formErrors, 'customerPhone')
        this.localCustomerPhone = val
        this.$emit('customerPhoneNumber', val)

        if (this.notification && val.trim() !== '') {
          this.$emit('notification', true)
        }
      },
    },
    statusFleetOrder() {
      return this.fleetTaskDetail?.status?.raw_type
    },
  },
  watch: {
    'fleetTaskDetail.customer.phone_number': {
      immediate: true,
      handler(newVal) {
        if (!this.isEditingPhone) {
          this.localCustomerPhone = this.cleanPhoneSafe(newVal) || ''
        }
      },
    },
    hasErrorNotification: {
      deep: true,
      immediate: true,
      handler(newVal) {
        if (newVal.show && newVal.message) {
          this.$set(this.formErrors, 'customerPhone', newVal.message)
        } else {
          this.$delete(this.formErrors, 'customerPhone')
        }
      },
    },
  },
  methods: {
    openNotificationGuide() {
      this.$bvModal.show('modal-guide-notification')
    },
    openDeliveryHub() {
      const url = process.env.VUE_APP_PORTAL_URL
      window.open(url, '_blank')
    },
  },
}
</script>

<style lang="scss" scoped>
.alert-message {
  margin-top: -10px;
  font-size: 12px;
}

.notif-icon {
  margin-right: 10px;
}

.circle-bg {
  width: 30px;
  height: 30px;
  background-color: #fff3e6; /* krem */
  border-radius: 50%;
  position: relative;
}

.bell-icon {
  font-size: 20px;
  &:before {
    color: #ffc107; /* kuning */
  }
}

.badge-dot {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 10px;
  height: 10px;
  background-color: #f44336; /* merah */
  border-radius: 50%;
  border: 2px solid #fff3e6;
}

.custom-card {
  .card-body {
    padding: 10px !important;
  }
  small.text-muted {
    font-size: 12px;
    position: relative;
    top: -12px;
  }
}

.mc-icon {
  font-size: 20px;
  vertical-align: middle;
  &:before {
    color: $blue;
  }
}

.display-phone-number {
  margin-left: 40px;
}
</style>
